import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CalendarDays, MapPin, Clock } from "lucide-react";

const EVENT_DATE = new Date("2026-04-30T18:30:00-04:00");

function useCountdown(target) {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft(target));
  useEffect(() => {
    const id = setInterval(() => setTimeLeft(getTimeLeft(target)), 1000);
    return () => clearInterval(id);
  }, [target]);
  return timeLeft;
}

function getTimeLeft(target) {
  const diff = target - new Date();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    expired: false,
  };
}

export default function Hero() {
  const { days, hours, minutes, seconds, expired } = useCountdown(EVENT_DATE);

  const scrollToRegister = () => {
    document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-background"
    >
      {/* Subtle grain texture overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      {/* Decorative gold line — top left */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
        className="absolute top-[45%] left-0 w-16 md:w-24 h-[1px] bg-primary origin-left hidden lg:block"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 min-h-screen flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-center w-full pt-28 pb-16 lg:pt-28 lg:pb-0">

          {/* LEFT — Text content */}
          <div className="lg:col-span-6 xl:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
              className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl font-light tracking-tight leading-[0.9] text-foreground mb-2"
            >
              Champagne,
            </motion.h1>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl font-light tracking-tight leading-[0.9] text-foreground mb-3"
            >
              Connection
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              className="flex items-baseline gap-3 mb-8"
            >
              <span className="text-3xl sm:text-4xl md:text-5xl font-light italic text-foreground/70">
                & "Just Be"
              </span>
              <div className="h-[1px] flex-1 bg-primary/30 max-w-[120px]" />
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4, ease: "easeOut" }}
              className="text-sm md:text-base font-['Outfit'] font-light text-muted-foreground leading-relaxed max-w-md mb-10"
            >
              Soft Power Costa Rica Retreat Preview at <span className="text-primary">SKYN CULTURE</span>.
              Join Antonio, Crystal Khalil & Dr. Nicole LaBeach for champagne, light bites, and connection.
            </motion.p>

            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45, ease: "easeOut" }}
              className="text-xs font-['Outfit'] font-semibold tracking-[0.3em] uppercase text-primary mb-6 block"
            >
              An Intimate Evening &mdash; April 30, Atlanta
            </motion.span>

            {/* Countdown */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
              data-testid="countdown-timer"
              className="mb-10"
            >
              {expired ? (
                <p className="text-lg font-['Outfit'] font-light text-primary tracking-wide">
                  The event has begun
                </p>
              ) : (
                <div className="flex items-start gap-3 sm:gap-4">
                  {[
                    { value: days, label: "Days" },
                    { value: hours, label: "Hrs" },
                    { value: minutes, label: "Min" },
                    { value: seconds, label: "Sec" },
                  ].map((unit, i) => (
                    <div key={unit.label} className="flex flex-col items-center">
                      <div className="w-16 sm:w-[72px] h-16 sm:h-[72px] border border-white/10 bg-white/[0.02] flex items-center justify-center">
                        <span className="text-2xl sm:text-3xl font-light tracking-tight text-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {String(unit.value).padStart(2, "0")}
                        </span>
                      </div>
                      <span className="text-[9px] font-['Outfit'] font-semibold tracking-[0.2em] uppercase text-muted-foreground mt-2">
                        {unit.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Event meta + CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: "easeOut" }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6"
            >
              <Button
                data-testid="hero-rsvp-btn"
                onClick={scrollToRegister}
                className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.2em] text-xs h-12 px-10 transition-all"
              >
                Reserve Your Spot
              </Button>
              <div className="flex items-center gap-4 text-xs font-['Outfit'] text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <CalendarDays strokeWidth={1} size={13} className="text-primary/60" />
                  <span>Apr 30</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock strokeWidth={1} size={13} className="text-primary/60" />
                  <span>6:30 PM</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin strokeWidth={1} size={13} className="text-primary/60" />
                  <span>Atlanta</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* RIGHT — Image collage */}
          <div className="lg:col-span-6 xl:col-span-5 relative hidden lg:block">
            {/* Main image — flower arch */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
              className="relative z-10 ml-auto w-[85%]"
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src="https://static.wixstatic.com/media/b91449_1772bd8ab34d4017b6f3515584fc92ec~mv2.jpg/v1/fill/w_800,h_1000,al_c,q_80,enc_avif,quality_auto/b91449_1772bd8ab34d4017b6f3515584fc92ec~mv2.jpg"
                  alt="SKYN CULTURE Facial Studio"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Gold corner accent */}
              <div className="absolute -top-3 -left-3 w-12 h-12 border-t border-l border-primary" />
              <div className="absolute -bottom-3 -right-3 w-12 h-12 border-b border-r border-primary" />
            </motion.div>

            {/* Secondary image — Crystal & Dr. Nicole (overlapping) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6, ease: "easeOut" }}
              className="absolute -bottom-8 -left-4 w-[45%] z-20"
            >
              <div className="aspect-[4/3] overflow-hidden border-2 border-background shadow-2xl">
                <img
                  src="https://static.wixstatic.com/media/b91449_75957ad6cdc14e7e879ad0ca4c3bd1be~mv2.png/v1/fill/w_800,h_778,al_c,q_85,enc_avif,quality_auto/b91449_75957ad6cdc14e7e879ad0ca4c3bd1be~mv2.png"
                  alt="Antonio Jones-Moore"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </motion.div>

            {/* Floating label */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="absolute -right-2 top-8 z-20 bg-primary text-primary-foreground px-4 py-2"
            >
              <span className="text-[10px] font-['Outfit'] font-semibold tracking-[0.15em] uppercase">
                Complimentary
              </span>
            </motion.div>
          </div>

          {/* Mobile hero image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:hidden relative"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="https://static.wixstatic.com/media/b91449_1772bd8ab34d4017b6f3515584fc92ec~mv2.jpg/v1/fill/w_800,h_600,al_c,q_80,enc_avif,quality_auto/b91449_1772bd8ab34d4017b6f3515584fc92ec~mv2.jpg"
                alt="SKYN CULTURE Facial Studio"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -top-2 -left-2 w-8 h-8 border-t border-l border-primary" />
            <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b border-r border-primary" />
          </motion.div>
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
    </section>
  );
}
